/**
 * Tenant company identity (SaaS "company details").
 *
 * Source of truth is the backend (`/api/workspace/company-profile`, persisted on
 * the Organization document). A tenant-scoped local cache keeps reads synchronous
 * for the rest of the app — notably report/PDF letterheads, which cannot await a
 * network round-trip.
 *
 * Flow:
 *   - hydrateCompanyProfile(): pull from backend into the local cache on login.
 *   - saveCompanyProfile(): write cache immediately + push to backend.
 *   - useCompanyProfile(): live React hook bound to the `company-profile` event.
 */
import { useEffect, useState } from 'react';
import { companyApi, hasAuthToken, type ApiCompanyProfile, type ApiCompanyPartner } from '../api';
import { loadDevJson, saveDevJson } from './devStorage';
import { mediaSrc } from './mediaUrl';
import { getOrganizationById, getTenantId } from './tenant';

export const COMPANY_PROFILE_KEY = 'company_profile_v1';
export const COMPANY_PROFILE_EVENT = 'company-profile';

export type CompanyPartner = ApiCompanyPartner;
export type CompanyProfile = ApiCompanyProfile;

export function emptyCompanyProfile(): CompanyProfile {
  return {
    legalName: '',
    displayName: '',
    tagline: '',
    email: '',
    phone: '',
    altPhone: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gstin: '',
    registrationNo: '',
    logoUrl: '',
    partners: [],
  };
}

/** Coerce any cached/partial blob into a fully-shaped profile. */
function normalize(input: unknown): CompanyProfile {
  const base = emptyCompanyProfile();
  if (!input || typeof input !== 'object') return base;
  const p = input as Partial<CompanyProfile>;
  return {
    ...base,
    ...p,
    logoUrl: mediaSrc(String(p.logoUrl ?? '')),
    country: (p.country ?? '').toString().trim() || 'India',
    partners: Array.isArray(p.partners)
      ? p.partners.map((x) => ({
          id: x?.id,
          name: String(x?.name ?? ''),
          role: String(x?.role ?? ''),
          phone: String(x?.phone ?? ''),
          email: String(x?.email ?? ''),
          share: String(x?.share ?? ''),
        }))
      : [],
  };
}

function stripDeliveryVersion(url: string): string {
  return url.replace(/\/v\d+(?=\/)/, '').split('?')[0];
}

/**
 * A profile round-trip must not drop a logo that is already on screen.
 * Empty server values are ignored when we already have one. A versioned
 * Cloudinary URL is also kept when the server sends the same file without
 * the version — that unversioned address is often a cached miss, so the
 * picture flashes and then disappears.
 */
function isInlineImage(value: string): boolean {
  return /^(data:|blob:)/i.test(value);
}

/** Filename delivery URL with no Cloudinary version — these are the links that 404. */
function isRebuiltFileUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value) || /\/v\d+\//.test(value)) return false;
  return /\/(?:shop|customer)_[A-Za-z0-9_-]+(?:_\d+)?\.[a-z0-9]+$/i.test(value.split('?')[0]);
}

export function keepVisibleLogo(previous: string, incoming: string): string {
  const prev = String(previous || '').trim();
  const next = String(incoming || '').trim();
  if (!next) return prev;
  if (!prev) return next;
  if (isInlineImage(prev) && !isInlineImage(next)) return prev;
  if (/\/v\d+\//.test(prev) && isRebuiltFileUrl(next) && stripDeliveryVersion(prev) === stripDeliveryVersion(next)) {
    return prev;
  }
  return next;
}

function emit() {
  try {
    window.dispatchEvent(new Event(COMPANY_PROFILE_EVENT));
  } catch {
    /* no window */
  }
}

/** Synchronous read from the tenant-scoped local cache. */
export function readCompanyProfile(): CompanyProfile {
  return normalize(loadDevJson<unknown>(COMPANY_PROFILE_KEY, null));
}

/** Write only the local cache (used by hydration; does not hit the network). */
export function cacheCompanyProfile(profile: CompanyProfile): void {
  saveDevJson(COMPANY_PROFILE_KEY, profile);
  emit();
}

/**
 * Persist the profile: update the local cache immediately, then push to the
 * backend when a session exists. Returns the saved (server-confirmed when
 * possible) profile.
 */
export async function saveCompanyProfile(profile: CompanyProfile): Promise<CompanyProfile> {
  const next = normalize(profile);
  cacheCompanyProfile(next);
  if (hasAuthToken()) {
    try {
      const res = await companyApi.update(next);
      const saved = normalize(res.profile);
      saved.logoUrl = keepVisibleLogo(next.logoUrl, saved.logoUrl);
      cacheCompanyProfile(saved);
      return saved;
    } catch {
      /* keep the local cache; a later save / hydrate will reconcile */
    }
  }
  return next;
}

/** Pull the latest profile from the backend into the local cache. */
export async function hydrateCompanyProfile(): Promise<CompanyProfile | null> {
  if (!hasAuthToken()) return null;
  try {
    const res = await companyApi.get();
    const saved = normalize(res.profile);
    saved.logoUrl = keepVisibleLogo(readCompanyProfile().logoUrl, saved.logoUrl);
    cacheCompanyProfile(saved);
    return saved;
  } catch {
    return null;
  }
}

/** Slug used for auto-generated borrower login emails — mirrors backend `companyDomainBase`. */
export function companyEmailDomainSlug(profile?: CompanyProfile): string {
  const p = profile ?? readCompanyProfile();
  const org = getTenantId() ? getOrganizationById(getTenantId()) : undefined;
  const base = (p.displayName || p.legalName || org?.name || '').trim();
  return String(base).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30) || 'company';
}

export function companyCustomerEmailDomain(profile?: CompanyProfile): string {
  return `${companyEmailDomainSlug(profile)}.com`;
}

/** Derive borrower portal email: name4 + phoneLast4 @ company-domain.com */
export function genCustomerLoginEmail(name: string, phone: string, profile?: CompanyProfile): string {
  const np = String(name || '').toLowerCase().replace(/[^a-z]/g, '').slice(0, 4) || 'user';
  const pp = String(phone || '').replace(/\D/g, '').slice(-4) || '0000';
  return `${np}${pp}@${companyCustomerEmailDomain(profile)}`;
}

/** The company display name for letterheads (legal name preferred). */
export function companyDisplayName(p: CompanyProfile): string {
  return (p.legalName || p.displayName || '').trim();
}

/** Single-line composed postal address. */
export function companyComposedAddress(p: CompanyProfile): string {
  const cityState = [p.city, p.state].map((s) => s.trim()).filter(Boolean).join(', ');
  const tail = [cityState, p.pincode.trim()].filter(Boolean).join(' - ');
  return [p.addressLine1, p.addressLine2, tail]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(', ');
}

export function useCompanyProfile(): {
  profile: CompanyProfile;
  refresh: () => void;
} {
  const [profile, setProfile] = useState<CompanyProfile>(() => readCompanyProfile());

  useEffect(() => {
    const fn = () => setProfile(readCompanyProfile());
    window.addEventListener(COMPANY_PROFILE_EVENT, fn);
    return () => window.removeEventListener(COMPANY_PROFILE_EVENT, fn);
  }, []);

  return { profile, refresh: () => setProfile(readCompanyProfile()) };
}
